import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  Response,
  PermissionEnum,
  Currency,
  apiAbortController,
  Country,
} from '@ui/src';
import { PspPartner } from '../../types/psp-partner';
import { PspAccount } from '../../types/psp-account';
import api from '../../api/api';
import { Account, Balance } from '../../types/account';
import {
  PaymentType,
  PurposeCodeListItem,
  PurposeCodeSettings,
} from '../types';

export interface PermissionResponse {
  permissions: PermissionEnum[];
}

export const dictionariesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransactionsDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/accounts?take=40'),
          fetchWithBQ('/v1/transactions-types?all=true'),
        ]);

        return dictionaries
          ? {
              data: {
                accounts: dictionaries[0].data,
                transactionTypes: dictionaries[1].data,
              },
            }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getTransactionsDictionariesWithoutGL: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/psp-accounts?all=true'),
        ]);

        return dictionaries
          ? { data: { pspPartnersAccounts: dictionaries[0].data } }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getTransactionsSettings: build.query<
      Response<{ key: string; data: { value: boolean } }>,
      void
    >({
      query: () => ({
        url: '/v1/settings?take=9999',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsRateSourceSettings: build.query<
      { data: Record<string, string> },
      void
    >({
      query: () => ({
        url: '/v1/settings/rate-sources',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsCurrencies: build.query<Response<Currency>, void>({
      query: () => ({
        url: '/v1/currencies?active=true&all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    getTransactionsCountries: build.query<Response<Country>, void>({
      query: () => ({
        url: '/v1/countries?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsRateSource: build.query<
      Response<{ name: string; description: string; id: string }>,
      void
    >({
      query: () => ({
        url: '/v1/rates-sources?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsAccounts: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPaymentTypes: build.query<Response<PaymentType>, void>({
      query: () => ({
        url: '/v1/payment-categories?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPspPartners: build.query<Response<PspPartner>, string>({
      query: (query) => ({
        url: `/v1/psp-partners${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsBaseCurrency: build.query<{ data: Currency }, void>({
      query: () => ({
        url: `/v1/currencies/base`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPspAccounts: build.query<Response<PspAccount>, string>({
      query: (query) => ({
        url: `/v1/psp-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPspPartnerById: build.query<{ data: PspPartner }, string>({
      query: (id) => ({
        url: `/v1/psp-partners/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPspAccountById: build.query<{ data: PspAccount }, string>({
      query: (id) => ({
        url: `/v1/psp-accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPrimaryAccounts: build.query<
      Response<PspAccount>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/primary-accounts${data.query || ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsAccountById: build.query<{ data: Account }, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPermissions: build.query<{ data: PermissionResponse }, void>(
      {
        query: () => ({
          url: '/v1/employees/whoami',
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getTransactionsAccountBalance: build.query<
      Response<Balance>,
      { id: string; currencyId: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?currencyId=${data.currencyId}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPurposeCodesSettings: build.query<
      Response<PurposeCodeSettings>,
      void
    >({
      query: () => ({
        url: '/v1/purpose-code-settings?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsPurposeCodesList: build.query<
      Response<PurposeCodeListItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/purpose-codes${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsWhitelabel: build.query<
      { data: { data: { value: Record<string, string> } } },
      void
    >({ query: () => ({ url: '/v1/settings/whitelabel' }) }),
  }),
});

export const {
  useGetTransactionsDictionariesQuery,
  useLazyGetTransactionsAccountsQuery,
  useLazyGetTransactionsPspPartnersQuery,
  useLazyGetTransactionsPspAccountsQuery,
  useLazyGetTransactionsPspPartnerByIdQuery,
  useLazyGetTransactionsPspAccountByIdQuery,
  useLazyGetTransactionsAccountByIdQuery,
  useLazyGetTransactionsPermissionsQuery,
  useLazyGetTransactionsPrimaryAccountsQuery,
  useLazyGetTransactionsAccountBalanceQuery,
  useLazyGetTransactionsDictionariesQuery,
  useGetTransactionsBaseCurrencyQuery,
  useLazyGetTransactionsBaseCurrencyQuery,
  useLazyGetTransactionsWhitelabelQuery,
  useLazyGetTransactionsPaymentTypesQuery,
  useLazyGetTransactionsCountriesQuery,
  useLazyGetTransactionsCurrenciesQuery,
  useLazyGetTransactionsRateSourceQuery,
  useLazyGetTransactionsDictionariesWithoutGLQuery,
  useLazyGetTransactionsRateSourceSettingsQuery,
  useLazyGetTransactionsSettingsQuery,
  useLazyGetTransactionsPurposeCodesListQuery,
  useLazyGetTransactionsPurposeCodesSettingsQuery,
} = dictionariesApi;

export const {
  endpoints: {
    getTransactionsDictionaries,
    getTransactionsPspPartnerById,
    getTransactionsPspAccountById,
    getTransactionsPermissions,
    getTransactionsPrimaryAccounts,
    getTransactionsAccountBalance,
    getTransactionsBaseCurrency,
    getTransactionsPaymentTypes,
    getTransactionsCountries,
    getTransactionsCurrencies,
    getTransactionsRateSource,
    getTransactionsDictionariesWithoutGL,
    getTransactionsRateSourceSettings,
    getTransactionsSettings,
    getTransactionsPurposeCodesList,
    getTransactionsPurposeCodesSettings,
  },
} = dictionariesApi;
